import {
  NATIVE_INJECTOR_TOKEN,
  Ng2StateDeclaration,
  StateDeclaration,
  StateRegistry,
  StateService,
  Transition,
  TransitionService,
  UIRouterGlobals,
  UIView,
} from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, Injector, NgZone, Type } from "@angular/core";
import equal from "fast-deep-equal";
import { firstValueFrom, noop } from "rxjs";
import { UiDrawerRef } from "@webapp/ui/drawer/abstracts/drawer-ref";
import { UiDrawerSize } from "@webapp/ui/drawer/drawer.models";
import { UiDrawerService } from "@webapp/ui/drawer/services/drawer.service";

type StateDrawerReturnTo = "auto" | "parent";

type DrawerStateDeclarationBase = Pick<StateDeclaration, "params" | "data" | "resolve" | "resolvePolicy"> & {
  name: string;
  url?: string;
  wrapperClassName?: string;
  mask?: boolean;
  size?: UiDrawerSize;
  width?: number | string;
  returnTo?: StateDrawerReturnTo;
  closable?: boolean;
  title?: string;
};

type ComponentDrawerStateDeclaration = DrawerStateDeclarationBase & {
  component: Type<unknown>;
};

type AbstractDrawerStateDeclaration = DrawerStateDeclarationBase & {
  abstract: true;
  component?: Type<unknown>;
};

type DrawerStateDeclaration = ComponentDrawerStateDeclaration | AbstractDrawerStateDeclaration;

@Component({
  selector: "abstract-drawer-view-container",
  template: `<ui-view />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbstractDrawerViewContainerComponent {}

export interface IStateSnapshot {
  name: string;
  params: {
    returnState?: string;
    [key: string]: unknown;
  };
}

export const drawerStateOpenerStack: IStateSnapshot[] = [];

const handleStateInitiatedDrawerCloseByStateChange = (routerGlobals: UIRouterGlobals): void => {
  // reset the opener stack if we are going to a previous state in the stack
  const newStateName = routerGlobals.current.name;
  const newStateParams = { ...routerGlobals.params };

  const stateIndex = drawerStateOpenerStack.findIndex((x) => x.name === newStateName && equal(x.params, newStateParams));
  if (stateIndex >= 0) {
    drawerStateOpenerStack.splice(stateIndex);
  }
};

const isDrawerState = (state: Pick<StateDeclaration, "data">): boolean =>
  state.data && Object.prototype.hasOwnProperty.call(state.data, "drawer") && state.data.drawer === true;

const ignoreDrawerOpener = (state: Pick<StateDeclaration, "data">): boolean =>
  state.data && Object.prototype.hasOwnProperty.call(state.data, "ignoreDrawerOpener") && state.data.ignoreDrawerOpener === true;

const getParentState = (stateRegistry: StateRegistry, state: StateDeclaration): StateDeclaration => stateRegistry.get("^", state);

const findFirstNonAbstractParentState = (stateRegistry: StateRegistry, state: StateDeclaration): StateDeclaration => {
  let returnState = getParentState(stateRegistry, state);
  while (returnState.abstract) {
    returnState = getParentState(stateRegistry, returnState);
  }

  return returnState;
};

const findFirstNonDrawerAndNonAbstractParentState = (stateRegistry: StateRegistry, state: StateDeclaration): StateDeclaration => {
  let drawerState = state;
  while (!isDrawerState(drawerState)) {
    drawerState = getParentState(stateRegistry, drawerState);
  }

  return findFirstNonAbstractParentState(stateRegistry, drawerState);
};

const popLast = (untilEqualsState: IStateSnapshot): void => {
  const lastOpener = drawerStateOpenerStack[drawerStateOpenerStack.length - 1];
  if (equal(lastOpener, untilEqualsState)) {
    drawerStateOpenerStack.pop();
    popLast(untilEqualsState);
  }
};

const handleStateInitiatedDrawerCloseByDrawerClose = (
  stateService: StateService,
  stateRegistry: StateRegistry,
  options: { beforeNavigationState: IStateSnapshot; returnTo: StateDrawerReturnTo; currentState: StateDeclaration }
): void => {
  const opener = drawerStateOpenerStack.pop();
  const { beforeNavigationState, returnTo } = options;
  const { returnState: customReturnState } = beforeNavigationState.params;
  let actualReturnState: string;

  let gotoPromise: Promise<unknown>;
  if (customReturnState && returnTo !== "parent") {
    actualReturnState = customReturnState;
    gotoPromise = stateService.go(customReturnState);
  } else if (opener && returnTo !== "parent") {
    actualReturnState = opener.name;
    gotoPromise = stateService.go(opener.name, opener.params);
  } else {
    // we have opened the URL directly and we don't know the opener,
    // so go to the first non-abstract parent state
    const returnState = findFirstNonDrawerAndNonAbstractParentState(stateRegistry, options.currentState);
    actualReturnState = returnState.name;
    gotoPromise = stateService.go(returnState);
  }

  gotoPromise.then(() => {
    // prevent endless loop when going back
    if (drawerStateOpenerStack.length) {
      popLast(beforeNavigationState);
    }

    // we are want to go to the parent state but logically we should have gone to the opener,
    // we need to reset the opener stack
    const wasNotSupposedToGoToParent = opener && (customReturnState || returnTo === "parent");
    if (wasNotSupposedToGoToParent) {
      const index = drawerStateOpenerStack.findIndex((x) => x.name === actualReturnState);
      if (index >= 0) {
        drawerStateOpenerStack.splice(index, 1);
      }
    }
  });
};

/**
 * When you use the Back button of the browser sometimes the Angular change detection is not triggered.
 * This is a hack that manually triggers the change detection. This should be removed when the hybrid
 * router is removed.
 */
const handleHybridRouterBackButtonIssue = (transition: Transition, state: DrawerStateDeclaration): (() => void) => {
  const injector = transition.injector();
  const transitionService = injector.get<TransitionService>(TransitionService);
  const ngZone = injector.get<NgZone>(NgZone);
  return transitionService.onSuccess({ to: `${state.name}.*` }, () => ngZone.run(noop));
};

const COPY_PROPS: (keyof DrawerStateDeclaration)[] = ["name", "url", "params", "resolve", "resolvePolicy"];

const createStateDrawer = (state: DrawerStateDeclaration, transition: Transition): UiDrawerRef => {
  const injector = transition.injector().get<Injector>(NATIVE_INJECTOR_TOKEN);
  const drawerService = injector.get(UiDrawerService);
  const ngZone = injector.get(NgZone);
  const routerGlobals = injector.get(UIRouterGlobals);
  const stateService = injector.get(StateService);
  const stateRegistry = injector.get(StateRegistry);

  // when using browser's Back/Forward button, change detection is not triggered
  const drawerRef = ngZone.run(() =>
    drawerService.create({
      uiContent: "component" in state ? state.component : AbstractDrawerViewContainerComponent,
      uiContentParams: transition.params("to"),
      uiClosable: state.closable !== false,
      uiFooter: null,
      uiWrapClassName: state.wrapperClassName || "",
      uiMask: state.mask,
      uiCloseOnNavigation: false,
      uiSize: state.size,
      uiWidth: state.width,
      uiTitle: state.title,
      uiProviders: [
        {
          provide: UIView.PARENT_INJECT,
          useValue: {
            fqn: stateRegistry.get("^", state.name).$$state().fqn(),
            context: stateRegistry.get(state.name).$$state(),
          },
        },
      ],
    })
  );
  drawerRef.afterClose.subscribe((reason) => {
    // we have called destroy() / close() on the modal reference, or clicked on the backdrop, or manually change the URL
    if (reason === "exiting") {
      const { transition } = routerGlobals;
      const promise = transition?.promise ?? Promise.resolve();
      promise.then(() => handleStateInitiatedDrawerCloseByStateChange(routerGlobals));
      return;
    }

    const { current, params } = routerGlobals;
    const beforeNavigationState: IStateSnapshot = {
      name: current.name,
      params: { ...params },
    };
    handleStateInitiatedDrawerCloseByDrawerClose(stateService, stateRegistry, { beforeNavigationState, returnTo: state.returnTo, currentState: current });
  });
  return drawerRef;
};

// States are resolves in a parent-to-child manner, but drawers are opened first on transition's
// success, so we need to maintain the order of state resolution when opening the drawers.
export const drawerOpenerQueue: (() => Promise<unknown>)[] = [];

export const drawer = (state: DrawerStateDeclaration): Ng2StateDeclaration => {
  if (state.name.indexOf(".") < 0) {
    throw new Error("Drawer states cannot be declared as root states");
  }

  let drawerRef: UiDrawerRef;
  let hybridRouterUnsubscribe: () => void;

  const drawerState: Ng2StateDeclaration = {
    data: {
      drawer: true,
      ...(state.data || {}),
    },
    component: AbstractDrawerViewContainerComponent,
    onEnter(transition: Transition) {
      const from = transition.from();
      const prevState = {
        name: from.name,
        params: transition.params("from"),
      };

      // if you want to prevent coming back to certain states,
      // you need to attach `ignoreDrawerOpener` data
      if (prevState.name && !ignoreDrawerOpener(from)) {
        drawerStateOpenerStack.push(prevState);
      }

      drawerOpenerQueue.push(() => {
        drawerRef = createStateDrawer(state, transition);
        return firstValueFrom(drawerRef.afterOpen);
      });

      hybridRouterUnsubscribe = handleHybridRouterBackButtonIssue(transition, state);
    },
    onExit() {
      // if we have a drawer instance it means we are exiting this state using `$state.go`
      // or clicking on an element with ui-sref directive
      if (drawerRef) {
        drawerRef.close("exiting");
        drawerRef = null;
      }

      if (hybridRouterUnsubscribe) {
        hybridRouterUnsubscribe();
        hybridRouterUnsubscribe = null;
      }
    },
  };

  // since we use the hybrid router, onEnter/onExit/onRetain hooks work like ng1 injectors
  // even if we use ng2 state declaration
  drawerState.onEnter.$inject = ["$transition$"];

  for (const prop of COPY_PROPS) {
    if (prop in state) {
      drawerState[prop] = state[prop];
    }
  }

  return drawerState;
};
